import { useState } from 'react';
import { Search, BookOpen, GraduationCap, Award, Globe, FileCheck, Building, Mail } from 'lucide-react';
import Footer from '../components/common/Footer';

export default function KennedyUniversityHonoraryPhD() {
  const [activeTab, setActiveTab] = useState('overview');

  const specializationAreas = [
    { category: "Area of Specialization I", fields: [
      "Advertising Management", "Information Technology", "Agriculture Management", "Intellectual Property Rights",
      "Air Transport Management", "Interior Management", "Animation Management", "International Finance Management",
      "Architectural Management", "International Management", "Asset Management", "International Trade",
      "Audit Management", "Investment Analysis Management", "Banking Management", "Investment Management",
      "Bio-Technology Management", "Labour Law Management", "BPO Management", "Library Management",
      "Business Administration Logistics Management", "Call Center Management", "Market Risk Management",
      "Chartered Finance Management", "Marketing Management", "Clinical Pharmacology", "Mass Communication"
    ]},
    { category: "Area of Specialization II", fields: [
      "Production Management", "Cyber Law Management", "Project Management", "Dairy Management",
      "Public Administration", "E-Commerce Public Relations Management", "E-Business Systems Purchasing Management",
      "Energy Management Retail Management", "Entrepreneur Management", "Risk and Insurance Management",
      "Environmental Studies Risk Management", "Equality Research Management", "Rural Management",
      "Event Management", "Safety Management", "Export Management", "Sales & Distribution Management"
    ]}
  ];

  const recognitions = [
    { name: "French Ministry of National Education", description: "Officially listed as an educational institution" },
    { name: "Florida Department of Education (USA)", description: "Annual Verification as a Religious College" },
    { name: "Ministry of Education, Culture & Higher Education", description: "Authorization and Recognition as a Degree-granting Higher Education Institution" },
    { name: "Ministry of Education & Higher Education", description: "Authorization and Recognition as a Degree-granting Higher Education Institution" },
    { name: "European Council of Leading Business Schools", description: "Registered and approved member institution" },
    { name: "International Association for Quality Assurance in Higher Education", description: "Full accreditation as a degree-granting institution" },
    { name: "United Nations Department of Economic and Social Affairs", description: "Listed in the United Nations Department of Economic and Social Affairs database" },
    { name: "Office of The Minister of Education, African Diaspora", description: "Accreditation and Recognition as a Degree-granting Higher Education Institution" },
    { name: "Quality Education Assurance Accreditation", description: "Full accreditation as a degree-granting institution" }
  ];

  const admissionRequirements = [
    { title: "Exceptional Contributions", description: "Demonstrated outstanding achievements in a specific field, such as education, science, arts, business, public service, or humanitarian efforts." },
    { title: "Alignment with Values", description: "Commitment to values of excellence, innovation, and social responsibility that align with Kennedy University's mission." },
    { title: "Leadership and Impact", description: "Proven leadership qualities with a significant positive impact on society, communities, or industries." },
    { title: "Nomination", description: "Candidates must be nominated by individuals, organizations, or the university's internal committee for consideration." },
    { title: "Recognition", description: "Acknowledgment by peers, organizations, or institutions for exemplary work and influence in the nominee's area of expertise." },
    { title: "Review Process", description: "Applications or nominations will undergo rigorous review by the university's selection committee to ensure alignment with program criteria." }
  ];

  return (
    <div className="min-h-screen bg-richblue-5">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-richblue-200 to-richblue-100 text-richblue-700 py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold mb-4">Honorary Doctorate of Philosophy (Ph.D.)</h1>
          <p className="text-xl text-richblue-500">Honoring exceptional individuals who shape the future through leadership and innovation</p>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-richblue-50 border-b border-richblue-300 shadow-sm sticky top-0 z-10">
        <div className="container mx-auto px-4">
          <div className="flex overflow-x-auto py-2 space-x-8">
            <button 
              onClick={() => setActiveTab('overview')}
              className={`px-1 py-2 font-medium whitespace-nowrap ${activeTab === 'overview' ? 'text-richblue-600 border-b-2 border-richblue-600' : 'text-richblue-400 hover:text-richblue-600'}`}
            >
              Overview
            </button>
            <button 
              onClick={() => setActiveTab('specializations')}
              className={`px-1 py-2 font-medium whitespace-nowrap ${activeTab === 'specializations' ? 'text-richblue-600 border-b-2 border-richblue-600' : 'text-richblue-400 hover:text-richblue-600'}`}
            >
              Specializations
            </button>
            <button 
              onClick={() => setActiveTab('admissions')}
              className={`px-1 py-2 font-medium whitespace-nowrap ${activeTab === 'admissions' ? 'text-richblue-600 border-b-2 border-richblue-600' : 'text-richblue-400 hover:text-richblue-600'}`}
            >
              Requirements
            </button>
            <button 
              onClick={() => setActiveTab('recognition')}
              className={`px-1 py-2 font-medium whitespace-nowrap ${activeTab === 'recognition' ? 'text-richblue-600 border-b-2 border-richblue-600' : 'text-richblue-400 hover:text-richblue-600'}`}
            >
              Recognition
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-12">
        {/* Overview Section */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="md:col-span-2 space-y-8">
              <div>
                <h2 className="text-3xl font-bold text-richblue-700 mb-4">Program Overview</h2>
                <p className="text-lg text-richblue-500 mb-4">
                  The Kennedy University Honorary Doctorate Program honors individuals who have made exceptional contributions to their fields and society. 
                  This prestigious recognition celebrates leadership, innovation, and a commitment to positive global impact.
                </p>
                <p className="text-lg text-richblue-500 mb-4">
                  By awarding honorary doctorates, Kennedy University fosters a legacy of inspiration and excellence, connecting changemakers who shape the future.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300">
                  <div className="bg-richblue-100 p-3 rounded-full w-fit mb-4">
                    <Award className="h-8 w-8 text-richblue-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-richblue-600 mb-2">Honoring Excellence</h3>
                  <p className="text-richblue-500">
                    Recognizing exceptional individuals for their leadership and impact.
                  </p>
                </div>
                <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300">
                  <div className="bg-richblue-100 p-3 rounded-full w-fit mb-4">
                    <Globe className="h-8 w-8 text-richblue-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-richblue-600 mb-2">Inspiring Change</h3>
                  <p className="text-richblue-500">
                    Celebrating innovators who shape a better future.
                  </p>
                </div>
                <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300">
                  <div className="bg-richblue-100 p-3 rounded-full w-fit mb-4">
                    <GraduationCap className="h-8 w-8 text-richblue-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-richblue-600 mb-2">A Lasting Legacy</h3>
                  <p className="text-richblue-500">
                    Joining a global network of changemakers.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300">
                <h3 className="text-xl font-semibold mb-4 text-richblue-600">Degrees Conferred</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-richblue-700">Kennedy University, France</h4>
                    <p className="text-richblue-500">Honorary Doctor of Philosophy in [Specific Field]</p>
                  </div>
                  <div>
                    <h4 className="font-medium text-richblue-700">Kennedy University of Baptist, USA</h4>
                    <p className="text-richblue-500">Honorary Doctor of Divinity in Philosophy with Specialization in [Specific Field]</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Specializations Section */}
        {activeTab === 'specializations' && (
          <div>
            <h2 className="text-3xl font-bold text-richblue-700 mb-6">Areas of Specialization</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {specializationAreas.map((area, index) => (
                <div key={index} className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300">
                  <h3 className="text-xl font-semibold text-richblue-600 mb-4">{area.category}</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {area.fields.map((field, fieldIndex) => (
                      <div key={fieldIndex} className="flex items-center">
                        <div className="h-2 w-2 bg-richblue-300 rounded-full mr-2"></div>
                        <span className="text-richblue-500 text-sm">{field}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Admission Requirements Section */}
        {activeTab === 'admissions' && (
          <div>
            <h2 className="text-3xl font-bold text-richblue-700 mb-6">Admission Requirements</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {admissionRequirements.map((req, index) => (
                <div key={index} className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300">
                  <h3 className="text-xl font-semibold text-richblue-600 mb-4">{req.title}</h3>
                  <p className="text-richblue-500">{req.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Accreditations Section */}
        {activeTab === 'recognition' && (
          <div>
            <h2 className="text-3xl font-bold text-richblue-700 mb-6">Institutional Recognitions</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {recognitions.map((recognition, index) => (
                <div key={index} className="bg-richblue-5 p-6 rounded-lg shadow-md border border-richblue-300">
                  <div className="mb-4">
                    <div className="bg-richblue-100 p-3 rounded-full w-fit">
                      <Award className="h-8 w-8 text-richblue-400" />
                    </div>
                  </div>
                  <h3 className="font-medium text-richblue-700 mb-2">{recognition.name}</h3>
                  <p className="text-richblue-500 text-sm">{recognition.description}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
      <Footer/>
    </div>
  );
}
